import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail,
  Send,
  CheckCircle,
  XCircle,
  User,
  MessageSquare,
  HelpCircle,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  ArrowRight,
  Leaf
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import MobileBottomNav from '../components/layout/MobileBottomNav';

const ContactUs = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: '',
    category: 'general',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null, 'success', 'error'

  const brandColor = '#ff660e';
  const brandColorLight = '#fff0e6';

  const contactCategories = [
    { id: 'general', name: 'General Inquiry' },
    { id: 'technical', name: 'Technical Support' },
    { id: 'account', name: 'Account Issues' },
    { id: 'payment', name: 'Payment & Escrow' },
    { id: 'dispute', name: 'Dispute Help' },
    { id: 'seller', name: 'Seller Support' }
  ];

  const socialLinks = [
    { platform: 'Facebook', icon: <Facebook className="w-5 h-5" />, url: 'https://facebook.com/ecomart', color: 'bg-blue-600 hover:bg-blue-700' },
    { platform: 'Twitter', icon: <Twitter className="w-5 h-5" />, url: 'https://twitter.com/ecomart', color: 'bg-black hover:bg-gray-800' },
    { platform: 'Instagram', icon: <Instagram className="w-5 h-5" />, url: 'https://instagram.com/ecomart', color: 'bg-pink-600 hover:bg-pink-700' },
    { platform: 'LinkedIn', icon: <Linkedin className="w-5 h-5" />, url: 'https://linkedin.com/company/ecomart', color: 'bg-blue-700 hover:bg-blue-800' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Simulate API call
    setTimeout(() => {
      console.log('Form submitted:', formData);
      setIsSubmitting(false);
      setSubmitStatus('success');
      
      // Reset form on success (but keep user data)
      if (submitStatus === 'success') {
        setFormData({
          name: user?.name || '',
          email: user?.email || '',
          subject: '',
          category: 'general',
          message: ''
        });
      }
    }, 1500);
  };

  const handleSupportLink = (type) => {
    switch (type) {
      case 'faq':
        navigate('/faq');
        break;
      case 'how-to-buy':
        navigate('/how-to-buy');
        break;
      case 'how-to-sell':
        navigate('/how-to-sell');
        break;
      case 'escrow':
        navigate('/escrow');
        break;
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-10 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6"
               style={{ backgroundColor: brandColorLight }}>
            <div className="flex items-center">
              <Leaf className="w-8 h-8" style={{ color: brandColor }} />
              <Mail className="w-8 h-8 ml-1" style={{ color: brandColor }} />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Contact EcoMart
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Have questions or need assistance? Reach out to our team and we'll get back to you within 24 hours.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
              
              {submitStatus === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Message Sent!</h3>
                  <p className="text-gray-600 mb-8">
                    Thank you for contacting EcoMart. We'll respond to your inquiry within 24 hours.
                  </p>
                  <button
                    onClick={() => setSubmitStatus(null)}
                    className="px-6 py-3 rounded-lg font-semibold"
                    style={{ 
                      backgroundColor: brandColor,
                      color: 'white'
                    }}
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                        style={{ focusRingColor: brandColor }}
                        placeholder="Enter your full name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                        style={{ focusRingColor: brandColor }}
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                      style={{ focusRingColor: brandColor }}
                    >
                      {contactCategories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                      style={{ focusRingColor: brandColor }}
                      placeholder="Brief description of your inquiry"
                    />
                  </div>

                  <div className="mb-8">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent resize-none"
                      style={{ focusRingColor: brandColor }}
                      placeholder="Please describe your inquiry in detail..."
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      * Required fields
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-8 py-3 rounded-lg font-semibold flex items-center transition-all hover:shadow-md disabled:opacity-50"
                      style={{ 
                        backgroundColor: brandColor,
                        color: 'white'
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <Send className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}

              {submitStatus === 'error' && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <XCircle className="w-5 h-5 text-red-600 mr-3" />
                    <p className="text-red-700">Failed to send message. Please try again.</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Support Links & Social Media */}
          <div className="space-y-6">
            {/* Support Links */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Support Links</h3>
              <div className="space-y-3">
                <button
                  onClick={() => handleSupportLink('faq')}
                  className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <HelpCircle className="w-5 h-5 text-gray-400 mr-3" />
                      <span className="text-gray-700">FAQ Center</span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-orange-500" />
                  </div>
                </button>
                
                <button
                  onClick={() => handleSupportLink('how-to-buy')}
                  className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <User className="w-5 h-5 text-gray-400 mr-3" />
                      <span className="text-gray-700">How to Buy Guide</span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-orange-500" />
                  </div>
                </button>
                
                <button
                  onClick={() => handleSupportLink('how-to-sell')}
                  className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <MessageSquare className="w-5 h-5 text-gray-400 mr-3" />
                      <span className="text-gray-700">How to Sell Guide</span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-orange-500" />
                  </div>
                </button>
                
                <button
                  onClick={() => handleSupportLink('escrow')}
                  className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-5 h-5 flex items-center justify-center mr-3">
                        <Leaf className="w-4 h-4 text-gray-400" />
                      </div>
                      <span className="text-gray-700">Escrow Protection Guide</span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-orange-500" />
                  </div>
                </button>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Follow Us</h3>
              <div className="flex space-x-3 mb-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.platform}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-white transition-colors ${social.color}`}
                    title={social.platform}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
              <p className="text-sm text-gray-600">
                Stay updated with tips, announcements, and community news
              </p>
            </div>

            {/* Contact Info */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Contact Information</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Email Support</p>
                  <a href="mailto:support@ecomart.com" className="font-medium"
                     style={{ color: brandColor }}>
                    support@ecomart.com
                  </a>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Response Time</p>
                  <p className="font-medium text-gray-900">Within 24 hours</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Business Hours</p>
                  <p className="font-medium text-gray-900">Mon-Fri, 9AM-6PM GMT</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Help Section */}
        <div className="mt-12 bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-2xl p-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Need Immediate Help?</h2>
            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
              Check our comprehensive guides before reaching out. Most questions are already answered in our help resources.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/faq')}
                className="px-6 py-3 bg-white text-orange-600 font-semibold rounded-lg hover:bg-orange-50 transition-colors flex items-center justify-center"
              >
                <HelpCircle className="w-5 h-5 mr-2" />
                Browse FAQ
              </button>
              <button
                onClick={() => navigate('/how-to-buy')}
                className="px-6 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors"
              >
                Visit Help Center
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default ContactUs;